import Cookies from 'js-cookie';

import { APIRes, CallArgs } from '@/types/api';
import { SESSION_COOKIE,SessionCookie } from '@/types/cookie';
import { envVarsProvider } from '@/utils/envVars/envVars';
import { handleSessionExpired } from '@/utils/session';

/**
 * Prepare and execute a fetch call with common API logic
 * If making a simple, non-streaming call, to extract JSON
 * from the response use {@linkcode call}. See that function for
 * a more detailed explanation of the args in `CallArgs`.
 *
 * @template T - The type of the request body
 * @template U - The type of the response data
 * @param args - An object containing the parameters needed to make the API call
 * @returns {Promise<Response>} A promise that resolves to the fetch Response object
 */
export const executeFetch = async <T = object, U = object>( args: CallArgs<T, U> ): Promise<Response> => {
  const {
    body,
    formData,
    params,
    isProtected,
    method,
    route,
    signal
  } = args;

  const urlParams = params ?
      Object.entries( params ).reduce(
        ( acc, [ key, value ] ) => {
          if ( value !== undefined && value !== null ) acc.append( key, String( value ) );
          return acc;
        }, new URLSearchParams() )
    : undefined;

  const apiBaseUrl = envVarsProvider.get( 'AIDPSTUDIO_API_URL' );
  const basePath = envVarsProvider.get( 'STUDIO_BASE_PATH' ) || '/';

  // If API URL is empty or relative, use same origin with base path
  const baseUrlPrefix = apiBaseUrl || ( window.location.origin + ( basePath === '/' ? '' : basePath ) );

  const url = baseUrlPrefix + route + ( urlParams ? `?${ urlParams.toString() }` : '' );

  // When sending FormData, omit Content-Type so the browser sets the
  // multipart boundary automatically.
  const headers: Headers = formData
    ? new Headers( { origin: window.location.origin } )
    : new Headers( { 'Content-Type': 'application/json', origin: window.location.origin } );

  if( isProtected ) {
    // if protected, get token from cookie directly
    const sessionCookieStr = Cookies.get( SESSION_COOKIE )?.valueOf();

    if ( !sessionCookieStr ) throw new Error( 'Protected route but not signed in' );

    const sessionCookie: SessionCookie = JSON.parse( sessionCookieStr );

    if( !sessionCookie.token ) throw new Error( 'Protected route but not signed in' );

    headers.append( 'Authorization', `Session ${ sessionCookie.token }` );
  }

  const fetchArgs: { [ key: string ]: unknown } = {
    headers,
    method
  };

  if ( formData ) {
    fetchArgs.body = formData;
  } else if ( body ) {
    fetchArgs.body = JSON.stringify( body );
  }
  if( signal ) fetchArgs.signal = signal;

  const res = await fetch( url, fetchArgs );

  if ( res.status === 401 && isProtected ) {
    // Distinguish auth-middleware 401 (session expired) from upstream proxy 401.
    // Auth middleware returns: { "detail": "Unauthorized" } or { "detail": "You are not logged in" }
    // Proxy/upstream 401s have different response shapes.
    try {
      const cloned = res.clone();
      const body = await cloned.json();

      if ( body?.detail === 'Unauthorized' || body?.detail === 'You are not logged in' ) {
        handleSessionExpired();
      }
    } catch {
      // If we can't parse the body, fall back to cookie check
      const cookieStr = Cookies.get( SESSION_COOKIE );

      if ( !cookieStr ) {
        handleSessionExpired();
      }
    }
  }

  return res;
};

/**
 * An api response type of 'error' will throw an Error. Only success data will be returned
 * This automatically unnests the `data` key from the APIRes object.
 *
 * @generator T type of request body
 * @generator U type of output
 * @param args
 * @param [args.body] - (optional) request body of type T
 * @param args.isProtected - is the route protected? will require args.token
 * @param args.method - request method enum CallMethodEnums
 * @param [args.resHandler] - (optional) custom response handler
 * @param args.route - request route
 * @param [args.signal] - (optional) AbortSignal for cancelling requests
 * @param [args.token] - (optional) token for protected routes
 * @returns generic U or object
 */
export const call = async <T = object, U = object>( args: CallArgs<T, U> ): Promise<U> => {
  const { resHandler } = args;

  const res = await executeFetch( args );

  let json: APIRes<U>;
  try {
    json = await res.json();
  } catch {
    if( res.ok ) return undefined as unknown as U;
    const error = new Error( `HTTP error ${ res.status }` );
    ( error as any ).statusCode = res.status;
    throw error;
  }

  if( !res.ok || json.type === 'error' ) {
    const error = new Error( json.message );
    // Attach status code to error for retry logic
    ( error as any ).statusCode = res.status;
    throw error;
  }

  if( resHandler ) {
    resHandler( json );
  }
  return json.data;
};

/**
 * Call API, throwing exception if response is not ok.
 *
 * @generator T type of request body
 * @generator U type of output
 * @param args
 * @param [args.body] - (optional) request body of type T
 * @param args.isProtected - is the route protected? will require args.token
 * @param args.method - request method enum CallMethodEnums
 * @param [args.resHandler] - (optional) custom response handler
 * @param args.route - request route
 * @param [args.signal] - (optional) AbortSignal for cancelling requests
 * @param [args.token] - (optional) token for protected routes
 * @returns generic U or object
 */
export const callJson = async <T = object, U = object>( args: Omit<CallArgs<T, U>, 'resHandler'> ): Promise<U> => {
  const res = await executeFetch( args );
  const json: U = await res.json();

  if( !res.ok ) {
    const error = new Error( `Status code: ${ res.status }; ${ JSON.stringify( json ) }` );
    // Attach status code to error for retry logic
    ( error as any ).statusCode = res.status;
    throw error;
  }

  return json;
};